import React, { useEffect, useState } from 'react';
import api from '../api';

const card = { background: 'white', border: '1px solid #d8e4dd', borderRadius: '8px', padding: '16px', boxShadow: '0 12px 30px rgba(24,47,38,0.08)' };
const muted = { color: '#61726b', fontSize: '13px' };

export default function BankingPage() {
  const [data, setData] = useState(null);
  const [provider, setProvider] = useState('gocardless');
  const [institutionId, setInstitutionId] = useState('ING_DE');
  const [institutionName, setInstitutionName] = useState('ING Deutschland');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setError('');
    try {
      const r = await api.get('/banking/status');
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Banking-Status konnte nicht geladen werden');
    }
  }

  async function createDraft() {
    setBusy(true);
    setMessage('');
    setError('');
    try {
      await api.post('/banking/connection/draft', { provider, institution_id: institutionId, institution_name: institutionName });
      setMessage('Verbindungsentwurf gespeichert. Noch keine Bankdaten und keine Tokens wurden abgerufen.');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Entwurf konnte nicht gespeichert werden');
    } finally {
      setBusy(false);
    }
  }

  async function previewSync(connectionId) {
    setBusy(true);
    setMessage('');
    setError('');
    try {
      const r = await api.post('/banking/sync/preview', { connection_id: connectionId });
      setMessage(r.data.message);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Sync-Vorschau konnte nicht gestartet werden');
    } finally {
      setBusy(false);
    }
  }

  const cfg = data?.config || {};
  const connections = data?.connections || [];
  const runs = data?.runs || [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
        <div>
          <h1 style={{ margin: 0, color: '#064734', fontSize: '25px' }}>Bank-Sync</h1>
          <p style={{ ...muted, margin: '4px 0 0' }}>Vorbereitung für ING/PSD2-Importe ohne gespeicherte Bank-Logins.</p>
        </div>
        <button onClick={load} style={button('#334155')}>Aktualisieren</button>
      </div>

      {error && <Notice tone="danger">{error}</Notice>}
      {message && <Notice>{message}</Notice>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
        <Stat title="Provider" value={cfg.provider || '-'} hint={cfg.mode || '-'} tone="blue" />
        <Stat title="Konfiguration" value={cfg.enabled ? 'bereit' : 'fehlt'} hint="API-Secrets + Redirect" tone={cfg.enabled ? 'green' : 'amber'} />
        <Stat title="Verbindungen" value={data?.summary?.connection_count || 0} hint={`${data?.summary?.active_count || 0} aktiv`} tone="slate" />
        <Stat title="Importe" value={data?.summary?.imports || 0} hint={data?.summary?.latest_import_at || 'noch keiner'} tone="slate" />
      </div>

      <section style={{ ...card, marginBottom: '12px', borderColor: cfg.enabled ? '#9dd8b3' : '#f7c56b', background: cfg.enabled ? '#f8fffb' : '#fffbeb' }}>
        <h2 style={h2}>Sicherheitsstatus</h2>
        <p style={{ color: '#334155', fontWeight: 800 }}>{cfg.note || 'Status wird geladen...'}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginTop: '10px' }}>
          <Check label="Secret ID" ok={cfg.secrets?.secret_id} />
          <Check label="Secret Key" ok={cfg.secrets?.secret_key} />
          <Check label="Redirect URL" ok={cfg.secrets?.redirect_url} />
        </div>
      </section>

      <section style={{ ...card, marginBottom: '12px' }}>
        <h2 style={h2}>ING-Verbindung vorbereiten</h2>
        <p style={{ ...muted, marginTop: '4px' }}>Dieser Schritt legt nur einen Entwurf an. Es wird noch keine Bankfreigabe gestartet.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
          <Field label="Provider">
            <select value={provider} onChange={e => setProvider(e.target.value)} style={input}>
              <option value="gocardless">GoCardless</option>
              <option value="finapi">finAPI</option>
              <option value="tink">Tink</option>
            </select>
          </Field>
          <Field label="Institution-ID">
            <input value={institutionId} onChange={e => setInstitutionId(e.target.value)} style={input} />
          </Field>
          <Field label="Name">
            <input value={institutionName} onChange={e => setInstitutionName(e.target.value)} style={input} />
          </Field>
          <button disabled={busy} onClick={createDraft} style={button('#064734')}>Entwurf speichern</button>
        </div>
      </section>

      <section style={{ ...card, marginBottom: '12px' }}>
        <h2 style={h2}>Verbindungen</h2>
        {!connections.length && <p style={muted}>Noch keine Bankverbindung vorbereitet.</p>}
        {connections.map(c => (
          <div key={c.id} style={{ borderTop: '1px solid #eef4f1', padding: '10px 0', display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px' }}>
            <div>
              <strong style={{ color: '#12231d' }}>{c.institution_name || c.institution_id}</strong>
              <div style={muted}>{c.provider} - {c.status} - Token: {c.token_hint || 'nicht gespeichert'}</div>
            </div>
            <button disabled={busy} onClick={() => previewSync(c.id)} style={button('#0f766e')}>Sync-Vorschau</button>
          </div>
        ))}
      </section>

      <section style={{ ...card, marginBottom: '12px' }}>
        <h2 style={h2}>Nächste Schritte</h2>
        <ol style={{ margin: '8px 0 0', paddingLeft: '20px', color: '#334155', fontWeight: 750 }}>
          {(data?.next_steps || []).map(step => <li key={step} style={{ marginBottom: '6px' }}>{step}</li>)}
        </ol>
      </section>

      <section style={card}>
        <h2 style={h2}>Sync-Protokoll</h2>
        {!runs.length && <p style={muted}>Noch keine Sync-Läufe.</p>}
        {runs.map(run => (
          <div key={run.id} style={{ borderTop: '1px solid #eef4f1', padding: '9px 0' }}>
            <strong style={{ color: '#12231d' }}>{run.status}</strong>
            <div style={muted}>{run.started_at} - {run.message}</div>
          </div>
        ))}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return <label style={{ display: 'grid', gap: '5px', color: '#334155', fontSize: '12px', fontWeight: 900 }}>{label}{children}</label>;
}

function Check({ label, ok }) {
  return <div style={{ border: '1px solid #d8e4dd', borderRadius: '8px', padding: '9px', background: 'white' }}><strong style={{ color: ok ? '#064734' : '#a16207' }}>{ok ? 'bereit' : 'fehlt'}</strong><div style={muted}>{label}</div></div>;
}

function Stat({ title, value, hint, tone = 'slate' }) {
  const colors = { blue: '#1d4ed8', green: '#064734', amber: '#a16207', slate: '#12231d' };
  return <div style={card}><div style={{ color: '#61726b', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase' }}>{title}</div><div style={{ color: colors[tone] || colors.slate, fontSize: '23px', fontWeight: 950, marginTop: '6px' }}>{value}</div><div style={{ ...muted, marginTop: '3px' }}>{hint}</div></div>;
}

function Notice({ children, tone = 'good' }) {
  const danger = tone === 'danger';
  return <div style={{ background: danger ? '#fff1ef' : '#eefbf3', color: danger ? '#9f1d12' : '#064734', border: `1px solid ${danger ? '#ffc9c2' : '#b7e4c7'}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontWeight: 800 }}>{children}</div>;
}

const h2 = { margin: 0, color: '#064734', fontSize: '17px' };
const input = { border: '1px solid #d8e4dd', borderRadius: '8px', padding: '10px 11px', background: 'white', color: '#12231d', fontWeight: 800, width: '100%', boxSizing: 'border-box' };
function button(bg) {
  return { background: bg, color: 'white', border: 'none', borderRadius: '8px', padding: '10px 12px', cursor: 'pointer', fontWeight: 900 };
}
